// src/utils/lossRunAuth.js
//
// Pure helpers behind the "Loss run authorization" block on the Loss Runs tab:
// the insured's signed letter authorizing the agency to pull loss runs, kept on
// FIG_Insureds as LossRunAuthOnFile (boolean), LossRunAuthSignedDate (dateOnly)
// and LossRunAuthVaultPath (text).
//
// On-file is three-state in the UI — Unknown / Yes / No — because an absent
// boolean is unknown, never false. A field the user left blank or on Unknown is
// never written; an edit sends only the fields that changed.

import { dateOnlyOf, daysBetween, boolOrNull } from './lossRunStatus';

export const AUTH_ON_FILE_OPTIONS = [
  { value: '', label: 'Unknown' },
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
];

const blank = v => v === null || v === undefined || String(v).trim() === '';

// FIG_Insureds row -> form strings. OnFile: '' | 'yes' | 'no'.
export function authFormFromInsured(insured) {
  const onFile = boolOrNull(insured?.LossRunAuthOnFile);
  return {
    OnFile: onFile === true ? 'yes' : onFile === false ? 'no' : '',
    SignedDate: dateOnlyOf(insured?.LossRunAuthSignedDate) || '',
    VaultPath: insured?.LossRunAuthVaultPath ? String(insured.LossRunAuthVaultPath) : '',
  };
}

// { field: message }; empty when valid. Signed date may not be in the future.
export function validateAuthForm(form, today) {
  const errors = {};
  const f = form || {};
  if (!blank(f.SignedDate)) {
    const d = dateOnlyOf(f.SignedDate);
    if (!d) errors.SignedDate = 'Enter a date as YYYY-MM-DD.';
    else if (daysBetween(d, dateOnlyOf(today) || dateOnlyOf(new Date())) < 0) errors.SignedDate = 'Signed date is in the future.';
  }
  if (!blank(f.OnFile) && !['yes', 'no'].includes(String(f.OnFile))) errors.OnFile = 'Pick Yes, No or Unknown.';
  return errors;
}

// Only non-blank values that differ from the row. Unknown (blank) on-file is
// never written, so a stored Yes/No cannot be un-set from this form.
export function authFieldsToWrite(form, insured) {
  const out = {};
  const f = form || {};
  const cur = authFormFromInsured(insured);
  if (!blank(f.OnFile) && f.OnFile !== cur.OnFile) out.LossRunAuthOnFile = f.OnFile === 'yes';
  const signed = dateOnlyOf(f.SignedDate);
  if (signed && signed !== cur.SignedDate) out.LossRunAuthSignedDate = signed;
  const path = blank(f.VaultPath) ? null : String(f.VaultPath).trim();
  if (path && path !== cur.VaultPath) out.LossRunAuthVaultPath = path;
  return out;
}

/**
 * Validate and write through the supplied api ({ updateItem } with figApi's
 * signature). Returns { ok:false, errors } | { ok:true, skipped:true } |
 * { ok:true, fields }.
 */
export async function saveAuthorization({ token, form, insured, today, api, listName = 'FIG_Insureds' }) {
  const errors = validateAuthForm(form, today);
  if (Object.keys(errors).length) return { ok: false, errors };
  const fields = authFieldsToWrite(form, insured);
  if (Object.keys(fields).length === 0) return { ok: true, skipped: true };
  await api.updateItem(token, listName, insured.id, fields);
  return { ok: true, fields };
}
